import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, [id]);

  if (!inv)
    return <div className="text-zinc-500 font-mono text-sm">Loading invoice…</div>;

  return (
    <div className="stagger-in max-w-4xl">
      <div className="no-print flex items-center justify-between mb-6">
        <Link to="/app/invoices" className="btn-ghost text-xs" data-testid="invoice-back">
          <ArrowLeft size={14} /> Back
        </Link>
        <button onClick={() => window.print()} className="btn-primary" data-testid="invoice-print">
          <Printer size={14} /> Print / PDF
        </button>
      </div>

      <div className="print-invoice card-sharp p-10" data-testid="invoice-detail">
        <div className="flex items-start justify-between mb-10 pb-8 border-b border-[var(--border)]">
          <div>
            <div className="font-display text-3xl">{settings.business_name || "Your Business"}</div>
            <div className="text-xs font-mono text-zinc-400 mt-1">
              {settings.address}
              {settings.phone ? ` · ${settings.phone}` : ""}
            </div>
            {settings.gst_number && (
              <div className="text-xs font-mono text-zinc-400">GST #: {settings.gst_number}</div>
            )}
          </div>
          <div className="text-right">
            <div className="label-mono">Invoice</div>
            <div className="font-display text-2xl mt-1" data-testid="invoice-number">
              {inv.invoice_number}
            </div>
            <div className="text-xs font-mono text-zinc-400 mt-1 uppercase tracking-wider">
              {inv.status}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div>
            <div className="label-mono mb-2">Bill to</div>
            <div className="font-mono text-sm text-white">{inv.client_name || "—"}</div>
            {inv.client_email && (
              <div className="font-mono text-sm text-zinc-400">{inv.client_email}</div>
            )}
            {inv.client_address && (
              <div className="font-mono text-sm text-zinc-400">{inv.client_address}</div>
            )}
          </div>
          <div className="md:text-right">
            <div className="label-mono mb-2">Issued</div>
            <div className="font-mono text-sm">
              {new Date(inv.created_at).toLocaleDateString("en-NZ")}
            </div>
            {inv.due_date && (
              <>
                <div className="label-mono mt-3 mb-2">Due</div>
                <div className="font-mono text-sm">{inv.due_date}</div>
              </>
            )}
          </div>
        </div>

        <div className="border border-[var(--border)] overflow-x-auto">
          <div className="min-w-[560px]">
          <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
            <div className="col-span-6 p-3">Description</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Qty</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Unit</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Amount</div>
          </div>
          {inv.items.map((it, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
            >
              <div className="col-span-6 p-3">{it.description}</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">{it.quantity}</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">
                {fmtMoney(it.unit_price)}
              </div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">
                {fmtMoney(it.quantity * it.unit_price)}
              </div>
            </div>
          ))}
          </div>
        </div>

        <div className="flex justify-end mt-8">
          <div className="w-72 space-y-2 font-mono text-sm">
            <Row label="Subtotal" value={fmtMoney(inv.subtotal)} />
            <Row label="GST 15%" value={fmtMoney(inv.gst)} />
            <div className="border-t border-[var(--border)] pt-2">
              <Row label="Total" value={fmtMoney(inv.total)} big />
            </div>
          </div>
        </div>

        {inv.notes && (
          <div className="mt-10 pt-6 border-t border-[var(--border)]">
            <div className="label-mono mb-2">Notes</div>
            <div className="text-sm text-zinc-300">{inv.notes}</div>
          </div>
        )}

        <div className="hi-vis-stripe h-1.5 -mx-10 -mb-10 mt-10" />
      </div>
    </div>
  );
}

function Row({ label, value, big }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span className={big ? "text-2xl text-white" : "text-base text-white"}>{value}</span>
    </div>
  );
}
